/**
 * Swiper Hard Sync
 * 
 * Forces Swiper to recalculate layout after images/fonts are loaded.
 * Eliminates clone mismatch, width drift, and timing shift.
 */
export function hardSync(swiper) {
  if (!swiper) return

  // Double RAF — ensures layout is fully computed
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      swiper.update()
      swiper.updateSlides()
      swiper.updateProgress()
      swiper.updateSize()
      // Reset to start position
      if (swiper.params.loop) {
        swiper.slideToLoop(0, 0, false)
      } else {
        swiper.slideTo(0, 0, false)
      }
    })
  })
}

/**
 * Init Swiper with hard sync guarantee + CRC-driven motion effects
 * @param {Function} SwiperConstructor - Swiper class
 * @param {HTMLElement} el - swiper container
 * @param {Object} params - swiper options (can be from CRC)
 * @returns {Swiper}
 */
export function initSynced(SwiperConstructor, el, params = {}) {
  const crc = params // params IS the CRC config

  const swiper = new SwiperConstructor(el, {
    ...params,
    observer: true,
    observeParents: true,
    on: {
      ...(params.on || {}),
      init: function () {
        hardSync(this)
        if (params.on?.init) params.on.init.call(this)
      },
      imagesReady: function () {
        hardSync(this)
      },
      // CRC-driven parallax/scale effects
      progress: function (s) {
        if (crc.parallax) {
          s.slides.forEach(slide => {
            const progress = slide.progress
            const scale = 1 - Math.abs(progress) * 0.05 * (crc.speedFactor || 1)
            const translate = progress * (crc.spaceBetween || 0) * 0.5
            slide.style.transform = `scale(${scale.toFixed(3)}) translateX(${translate.toFixed(1)}px)`
          })
        }
        if (params.on?.progress) params.on.progress.call(this, s)
      },
    },
  })
  return swiper
}

/**
 * Sync all swiper instances in a component
 */
export function syncAll(...swipers) {
  swipers.filter(Boolean).forEach(hardSync)
}
